import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { setSection, updateSection } from "../../../assignmentSlice";
import { getStartingIndex } from "../../../../../utils/resumeUtils";
import { parseScrambleQuestion } from "../../../../../utils/parseUtils";

import ScrambleButton from "../../widgets/ScrambleButton";
import CommonPage from "../../common/CommonPage";

const ScramblePage = ({ setCurrentStep, isPreview, isReview }) => {
  const dispatch = useDispatch();
  const { section } = useSelector((state) => state.assignment);

  const [pageList, setPageList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [wordList, setWordList] = useState([]);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const currentPage = pageList[currentIndex] || {
    question: "",
    hint: "",
    answer: "",
  };

  useEffect(() => {
    if (currentPage.parsedQuestion) {
      const scrambleElement = currentPage.parsedQuestion.find(
        (element) => element.type === "scramble"
      );
      if (scrambleElement) {
        setWordList(scrambleElement.content);
      }
    }
  }, [currentIndex, currentPage]);

  useEffect(() => {
    if (pageList.length > 0) return;

    if (!isPreview && section.answer.length > 0) {
      setPageList(section.answer);
      setCurrentIndex(getStartingIndex(section.resumeIndex));
      return;
    }
    setPageList(
      section.question.data.map((question) => {
        const parsedQuestion = parseScrambleQuestion(question.question);

        // Extract clean answer (remove context text)
        let cleanAnswer = question.answer;
        let scrambleContent;

        for (let i = 0; i < parsedQuestion.length; i++) {
          if (parsedQuestion[i].type === "text") {
            cleanAnswer = cleanAnswer.replace(parsedQuestion[i].content.trim(), "");
          } else if (parsedQuestion[i].type === "scramble") {
            scrambleContent = parsedQuestion[i].content;
          }
        }

        // Structure the answer as word objects
        cleanAnswer = cleanAnswer.trim().split(/\s+/).map((word) => {
          return {
            text: word,
            index: scrambleContent.find((content) => content.text === word)?.index || 0
          };
        });

        return {
          ...question,
          parsedQuestion: parsedQuestion,
          cleanAnswer: cleanAnswer,
          input: [],
          isSubmitted: false,
          isCorrect: false,
        };
      })
    );
    setCurrentIndex(0);
  }, [section]);

  const handleClickWord = (word) => {
    if (currentPage.isSubmitted) return;

    const prevInput = currentPage.input || [];
    const existingIndex = prevInput.findIndex((item) => item.index === word.index);

    let currentInput;
    if (existingIndex >= 0) {
      currentInput = prevInput.filter((item) => item.index !== word.index);
    } else {
      currentInput = [...prevInput, word];
    }

    const newPageList = pageList.map((prevPage, index) => {
      if (index === currentIndex) {
        return {
          ...prevPage,
          input: currentInput,
        };
      }
      return prevPage;
    });
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));
  };

  const handleNextPage = () => {
    if (currentIndex < pageList.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      let isAllAnswered = true;

      pageList.forEach((page) => {
        if (!page.isSubmitted) {
          isAllAnswered = false;
        }
      });

      if (isAllAnswered) {
        if (!isPreview && !isReview) {
          dispatch(
            updateSection({
              sectionId: section.id,
              data: { is_finished: true, answer: pageList },
            })
          );
        }
        alert("학습이 완료되었습니다.");
        setCurrentStep(2);
      } else {
        alert("문제를 덜 풀었습니다.");
      }
    }
  };

  const handlePreviousPage = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleSubmit = () => {
    if (currentPage.isSubmitted) return;

    const input = currentPage.input || [];

    if (input.length < currentPage.cleanAnswer.length) {
      alert("모든 단어를 선택해주세요.");
      return;
    }

    // Check if answer is correct (compare word objects in order)
    const correct = currentPage.cleanAnswer.every((answer, index) => {
      return answer.text === input[index]?.text;
    });

    const newPageList = pageList.map((page, index) => {
      if (index === currentIndex) {
        return {
          ...page,
          isSubmitted: true,
          isCorrect: correct,
        };
      }
      return page;
    });

    setOverlayVisible(true);
    setIsCorrect(correct);
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));

    setTimeout(() => {
      setOverlayVisible(false);
    }, 600);
  };

  return (
    <CommonPage
      currentIndex={currentIndex}
      handleNextPage={handleNextPage}
      handlePreviousPage={handlePreviousPage}
      currentPage={currentPage}
      totalNo={pageList.length}
      overlayVisible={overlayVisible}
      isCorrect={isCorrect}
      handleSubmit={handleSubmit}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "40px",
          width: "90%",
        }}
      >
        <div style={{ fontSize: "20px" }}>주어진 단어를 올바른 순서로 배열하시오.</div>
        <div
          style={{
            fontSize: "18px",
            textAlign: "center",
            lineHeight: "1.5",
            color: "#666",
          }}
        >
          {currentPage.hint}
        </div>

        {/* Display sentence with blanks for scrambled section */}
        <div
          style={{
            minHeight: "60px",
            padding: "15px 20px",
            fontSize: "24px",
            borderBottom: "1px solid #aaa",
            width: "80%",
            textAlign: "center",
            lineHeight: "1.5",
          }}
        >
          {currentPage.parsedQuestion && (
            <div style={{ wordBreak: "break-word", display: "inline" }}>
              {currentPage.parsedQuestion
                .map((element) => {
                  if (element.type === "scramble") {
                    const blankLength = element.length;
                    const userAnswer = currentPage.input || [];

                    return userAnswer
                      .map((answer) => answer.text)
                      .join(" ")
                      .concat(" ")
                      .padEnd(blankLength, "_");
                  } else {
                    return element.content;
                  }
                })
                .join(" ")}
            </div>
          )}
        </div>

        {/* Scrambled word buttons */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px",
            justifyContent: "center",
            maxWidth: "700px",
          }}
        >
          {wordList.length > 0 && (
            <ScrambleButton
              wordList={wordList}
              currentPage={currentPage}
              handleClickWord={handleClickWord}
              type={"sentence"}
            />
          )}
        </div>

        {/* Show correct answer after submission if incorrect */}
        {currentPage.isSubmitted && !currentPage.isCorrect && (
          <div
            style={{
              fontSize: "18px",
              color: "#d32f2f",
              textAlign: "center",
            }}
          >
            정답: {currentPage.answer}
          </div>
        )}
      </div>
    </CommonPage>
  );
};

export default ScramblePage;
